import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import useMutate from "../../../../../hooks/useMutate";
import {
  updateWorkflowIncolumn,
  updateWorkflowStep,
} from "../../../../../services/apiCards";

function getResponseData(queryData) {
  return queryData?.data?.data?.responseData || [];
}

function setResponseData(queryData, responseData) {
  if (!queryData?.data?.data) {
    return queryData;
  }

  return {
    ...queryData,
    data: {
      ...queryData.data,
      data: {
        ...queryData.data.data,
        responseData,
      },
    },
  };
}

export function useKanbanBoardDrag({ cards, id }) {
  const [localCards, setLocalCards] = useState(cards);
  const queryClient = useQueryClient();
  const rollbackSnapshotRef = useRef(cards);

  useEffect(() => {
    setLocalCards(cards);
  }, [cards]);

  const rollbackCards = useCallback(() => {
    const snapshotCards = rollbackSnapshotRef.current;
    setLocalCards(snapshotCards);
    queryClient.setQueryData(["cards", id], (oldData) => {
      const existingCards = getResponseData(oldData);

      if (!existingCards.length) return oldData;

      const updatedResponseData = existingCards.map((card) => {
        const originalCard = snapshotCards.find(
          (existingCard) => existingCard.card.card_id === card.card.card_id
        );
        return originalCard || card;
      });

      return setResponseData(oldData, updatedResponseData);
    });
  }, [id, queryClient]);

  const { mutate: updateMutate } = useMutate(
    updateWorkflowStep,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({ queryKey: ["cards", id] });
    },
    rollbackCards
  );

  const { mutate: reorderMutate } = useMutate(
    updateWorkflowIncolumn,
    [],
    () => {},
    () => {
      queryClient.invalidateQueries({ queryKey: ["cards", id] });
    },
    rollbackCards
  );

  const onDragEnd = useCallback(
    (result) => {
      const { source, destination, draggableId } = result;

      if (!destination) return;
      if (destination.droppableId === "todo-column") {
        toast.error("Cannot move a Regular task to Quick Tasks");
        return;
      }

      const isSameColumn = source.droppableId === destination.droppableId;
      if (isSameColumn && source.index === destination.index) return;

      const pristineOriginalCards = JSON.parse(JSON.stringify(localCards));
      rollbackSnapshotRef.current = pristineOriginalCards;

      const currentCards = [...localCards];

      const destinationCards = currentCards
        .filter(
          (card) => `${card.card.workflow_step_id}` === destination.droppableId
        )
        .sort(
          (a, b) =>
            (a?.card?.card_sequence ?? 0) - (b?.card?.card_sequence ?? 0)
        );

      const updated = Array.from(destinationCards);
      let movedCard = currentCards.find(
        (card) => card.card.card_id === parseInt(draggableId, 10)
      );
      if (!movedCard) return;

      if (!isSameColumn) {
        movedCard = {
          ...movedCard,
          card: {
            ...movedCard.card,
            workflow_step_id: Number(destination.droppableId),
          },
        };
      }

      if (isSameColumn) {
        updated.splice(source.index, 1);
      }
      updated.splice(destination.index, 0, movedCard);

      updated.forEach((card, index) => {
        card.card.card_sequence = index + 1;
      });

      const afterCard = updated[destination.index - 1];
      const nextCard = updated[destination.index + 1];

      const reorderPayload = {
        card_id: Number(draggableId),
        workflow_step_id: Number(destination.droppableId),
        after_card_id: afterCard?.card?.card_id || null,
        next_card_id: nextCard?.card?.card_id || null,
      };

      const newLocalCards = currentCards.map((card) => {
        if (card.card.card_id === parseInt(draggableId, 10)) {
          return movedCard;
        }

        if (`${card.card.workflow_step_id}` === destination.droppableId) {
          const updatedCard = updated.find(
            (item) => item.card.card_id === card.card.card_id
          );
          return updatedCard || card;
        }

        return card;
      });

      setLocalCards(newLocalCards);

      queryClient.setQueryData(["cards", id], (oldData) => {
        const existingCards = getResponseData(oldData);

        if (!existingCards.length) return oldData;

        const newQueryCards = existingCards.map((card) => {
          if (card.card.card_id === parseInt(draggableId, 10)) {
            return movedCard;
          }

          if (`${card.card.workflow_step_id}` === destination.droppableId) {
            const updatedCard = updated.find(
              (item) => item.card.card_id === card.card.card_id
            );
            return updatedCard || card;
          }

          return card;
        });

        return setResponseData(oldData, newQueryCards);
      });

      if (!isSameColumn) {
        updateMutate({
          card_id: Number(draggableId),
          workflow_step_id: Number(destination.droppableId),
        });
        return;
      }

      reorderMutate(reorderPayload);
    },
    [id, localCards, queryClient, reorderMutate, updateMutate]
  );

  return {
    localCards,
    onDragEnd,
  };
}
